/**
 * Single hypothesis item component
 * Displays a testable performance hypothesis with change, metric, impact, mechanism, and risk
 */

import { colors } from '@/lib/design-tokens';
import type { PerformanceHypothesis } from '@/types';

export interface HypothesisItemProps {
  hypothesis: PerformanceHypothesis;
  index: number;
}

export function HypothesisItem({ hypothesis, index }: HypothesisItemProps) {
  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
      {/* Header with Index */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold flex-shrink-0"
          style={{
            backgroundColor: colors.brand.primarySoft,
            color: colors.brand.primary,
          }}
        >
          {index + 1}
        </div>
        <p className="text-sm font-medium text-gray-900 leading-relaxed">
          {hypothesis.hypothesis}
        </p>
      </div>

      {/* Details Grid */}
      <div className="ml-9 space-y-2">
        {/* Change */}
        <div className="flex items-start gap-2 text-xs">
          <span className="text-gray-500 font-semibold uppercase tracking-wider w-20 flex-shrink-0">
            Change:
          </span>
          <span className="text-gray-700">{hypothesis.change}</span>
        </div>

        {/* Metric + Expected Impact */}
        <div className="flex items-start gap-2 text-xs">
          <span className="text-gray-500 font-semibold uppercase tracking-wider w-20 flex-shrink-0">
            Metric:
          </span>
          <span className="text-gray-700">
            {hypothesis.metric}
            <span
              className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium"
              style={{
                backgroundColor: colors.status.successSoft,
                color: colors.status.success,
              }}
            >
              {hypothesis.expectedImpact}
            </span>
          </span>
        </div>

        {/* Mechanism */}
        <div className="flex items-start gap-2 text-xs">
          <span className="text-gray-500 font-semibold uppercase tracking-wider w-20 flex-shrink-0">
            Mechanism:
          </span>
          <span className="text-gray-600">{hypothesis.mechanism}</span>
        </div>

        {/* Risk */}
        <div className="flex items-start gap-2 text-xs">
          <span className="text-gray-500 font-semibold uppercase tracking-wider w-20 flex-shrink-0">
            Risk:
          </span>
          <span
            className="px-2 py-0.5 rounded text-xs"
            style={{
              backgroundColor: colors.status.warningSoft,
              color: colors.status.warning,
            }}
          >
            {hypothesis.risk}
          </span>
        </div>
      </div>
    </div>
  );
}
